import {
  createContext,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

/** The frames are drawn at 1280×720; every size on the big screen is a size at this stage. */
export const STAGE_WIDTH = 1280;
export const STAGE_HEIGHT = 720;

export interface StageBox {
  /** Logical size of the stage the layouts draw on: at least 1280×720, the viewport's shape. */
  width: number;
  height: number;
  scale: number;
}

// eslint-disable-next-line react-refresh/only-export-components -- the stage maths, shared with FE-L1b's preview
export const stageFor = (boxWidth: number, boxHeight: number): StageBox => {
  if (!(boxWidth > 0) || !(boxHeight > 0))
    return { width: STAGE_WIDTH, height: STAGE_HEIGHT, scale: 1 };
  const scale = Math.min(boxWidth / STAGE_WIDTH, boxHeight / STAGE_HEIGHT);
  return { width: boxWidth / scale, height: boxHeight / scale, scale };
};

const StageContext = createContext<StageBox>(stageFor(0, 0));

/** The stage's logical size, for layouts that fit their content to it. */
// eslint-disable-next-line react-refresh/only-export-components -- the stage and its reader belong together
export const useStageBox = (): StageBox => useContext(StageContext);

/**
 * Fills its parent and draws the big screen on a logical stage scaled to fit (TECH-867 § Views B: checked at
 * 1024×768, 1280×720 and 1920×1080). The stage is 1280×720 at 16:9 and keeps the viewport's shape
 * otherwise (1280×960 at 4:3), so nothing is letterboxed and nothing overflows: the layouts are flex boxes
 * that take the extra room. The host view's preview (FE-L1b, RESOLUTIONS L27) puts the same stage in a
 * small box.
 */
export function BigScreenStage({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<StageBox>(() => stageFor(0, 0));

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const next = stageFor(el.clientWidth, el.clientHeight);
      setBox(prev =>
        prev.width === next.width && prev.height === next.height && prev.scale === next.scale
          ? prev
          : next,
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div
      ref={ref}
      className="relative h-full w-full overflow-hidden"
      data-testid="big-screen-stage"
    >
      <div
        className="absolute left-0 top-0 origin-top-left"
        style={{ width: box.width, height: box.height, transform: `scale(${box.scale})` }}
        data-stage-width={Math.round(box.width)}
        data-stage-height={Math.round(box.height)}
      >
        <StageContext.Provider value={box}>{children}</StageContext.Provider>
      </div>
    </div>
  );
}
